import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { EnvironmentService } from 'src/app/shared/common/environment/environment.service';

import { UtilitiesService } from '../common/utility/utilities.service';
import { Field, FieldStatus, GroupField, LayoutFieldType } from './field';
import { FieldDefinition } from './field/fieldDefinition';
import { InsertNewFieldOptions, Template } from './template';
import { GeoTemplate, GeoTemplateDeltas, GeoTemplates } from './templateDTO';
import { UUID } from './uuid';

export interface AvailableColor {
    id: number;
    color: string;
}

@Injectable({
    providedIn: 'root'
})
export class TemplateService {
    constructor(private http: HttpClient, private env: EnvironmentService, private translate: TranslationService) {}

    getTemplates(projectId: string = null, workspaceId?: string): Promise<Template[]> {
        let prefixPath = '/projects/' + projectId;
        let templatePath = prefixPath + '/templates';

        if (workspaceId) {
            templatePath += '?projectId=' + projectId + '&workspaceId=' + workspaceId;
        }

        let headers = {
            headers: {
                Range: 'items=0-'
            }
        };

        return new Promise((resolve, reject) => {
            lastValueFrom(this.http.get<GeoTemplates>(this.env.apiUrl + templatePath, headers)).then(templates => {
                if (templates) {
                    let modeledTemplate = templates.items.map(template => Template.fromDTO(template, this.translate));
                    resolve(modeledTemplate);
                } else {
                    resolve([]);
                }
            });
        });
    }

    getTemplateById(projectId: string, templateId: string): Promise<Template> {
        let templatePath = '/projects/' + projectId + '/templates/' + templateId;
        return lastValueFrom(this.http.get<GeoTemplate>(this.env.apiUrl + templatePath)).then(response =>
            Template.fromDTO(response, this.translate)
        );
    }

    getTemplatesByQuery(projectId: string, templateIds: string[]): Promise<Template[]> {
        return lastValueFrom(
            this.http.get<GeoTemplates>(
                this.env.apiUrl + '/projects/' + projectId + '/templates/_query?filter=' + `$Id in (${templateIds})`
            )
        ).then(response => {
            let responseTemplate = response.items.map(responseItem => Template.fromDTO(responseItem, this.translate));
            return responseTemplate;
        });
    }

    getTemplateBySeriesId(projectId: string, templateSeriesId: string): Promise<Template[]> {
        let templatePath = '/projects/' + projectId + '/templates/series/' + templateSeriesId;
        return lastValueFrom(this.http.get<GeoTemplates>(this.env.apiUrl + templatePath)).then(response => {
            let responseTemplate = response.items.map(responseItem => Template.fromDTO(responseItem, this.translate));
            return responseTemplate;
        });
    }

    getLatestPublishedTemplateBySeriesId(projectId: string, templateSeriesId: string): Promise<Template> {
        let templatePath = `/projects/${projectId}/templates/series/latestpublishedtemplates?seriesIds=${templateSeriesId}`;
        return lastValueFrom(this.http.get<GeoTemplates>(this.env.apiUrl + templatePath)).then(response => {
            return response.items[0] ? Template.fromDTO(response.items[0], this.translate) : null;
        });
    }

    getTemplateFields(projectId: string, templateSeriesId: string): Promise<any[]> {
        let templatePath = '/projects/' + projectId + '/templates/series/' + templateSeriesId + '/latest';
        return lastValueFrom(this.http.get<GeoTemplate>(this.env.apiUrl + templatePath)).then(response => {
            if (response && response.template) {
                const fields = response.template['fields'].filter((field: any) => {
                    if (field.type !== LayoutFieldType.PageHeader && field.type !== LayoutFieldType.Group) {
                        return field;
                    }
                });
                return fields;
            }
            return [];
        });
    }

    getTemplatesWorkspacesMappingListPaged(
        projectId: string,
        startIndex: number = 0,
        pageSize: number = 100,
        templateName?: string,
        workspaceName?: string
    ): Promise<GeoTemplate[]> {
        let path = `/projects/${projectId}/templates/_query`;

        const headers = new HttpHeaders({
            Range: `items=${startIndex}-${startIndex + pageSize - 1}`
        });

        let filter = "($IsPublished eq 'true') and ($WorkspaceStatus eq 'Active')";

        if (templateName && workspaceName) {
            filter += ` and ($Name cn '${templateName}' or $WorkspaceName cn '${workspaceName}')`;
        } else if (templateName) {
            filter += ` and ($Name cn '${templateName}')`;
        } else if (workspaceName) {
            filter += ` and ($WorkspaceName cn '${workspaceName}')`;
        }

        const params = {
            filter: filter,
            minified: 'true',
            bringLatestVersionOnly: 'true'
        };

        return lastValueFrom(this.http.get<GeoTemplates>(this.env.apiUrl + path, { params, headers })).then(
            response => {
                if (response) {
                    return response.items;
                }
            }
        );
    }

    createTemplate(
        projectId: string,
        template: GeoTemplate & {
            hasActiveTasks: boolean;
        },
        layerId?: string
    ): Promise<Template> {
        let templatePath = '/projects/' + projectId + '/templates' + (layerId ? '?layerId=' + layerId : '');
        return new Promise((resolve, reject) => {
            lastValueFrom(this.http.post<GeoTemplate>(this.env.apiUrl + templatePath, template))
                .then(templateResponse => {
                    resolve(Template.fromDTO(templateResponse, this.translate));
                })
                .catch(e => reject(e));
        });
    }

    getAllPublishedTemplates(projectId: string, seriesId: string): Promise<Template[]> {
        let templatePath =
            '/projects/' +
            projectId +
            "/templates/_query?filter=($SeriesId EQ '" +
            seriesId +
            "') AND ($IsPublished EQ 'true')";
        return lastValueFrom(this.http.get<GeoTemplates>(this.env.apiUrl + templatePath)).then(response => {
            if (response) {
                response.items = response.items.map(item => Template.fromDTO(item, this.translate));
                return response.items as Template[];
            }
        });
    }

    // Get all the removed/archived fields from a template series - Used to make sure field names (identifier for fields in field app)
    // are unique across all versions of a template (not just the current version)
    getTemplateDeltas(
        templateSeriesId: string,
        projectId: string,
        lowerVersion?: number
    ): Promise<{ archived: Field[]; unPublished: Field[] }> {
        let path = '/projects/' + projectId + '/templates/deltas' + '?seriesId=' + templateSeriesId;
        let queryWithLowerVersion = lowerVersion ? '&lowerVersion=' + lowerVersion : '';
        let deltas: {
            archived: Field[];
            unPublished: Field[];
        } = {
            archived: [],
            unPublished: []
        };
        return lastValueFrom(this.http.get<GeoTemplateDeltas>(this.env.apiUrl + path + queryWithLowerVersion)).then(
            template => {
                if (template) {
                    deltas.archived = template.removals.map(field => new Field(this.translate, field));

                    deltas.unPublished = template.additions.map(field => new Field(this.translate, field));
                }
                return deltas;
            }
        );
    }

    updateTemplate(
        projectId: string,
        template: GeoTemplate & {
            hasActiveTasks: boolean;
        },
        id: string
    ): Promise<Template> {
        let templatePath = '/projects/' + projectId + '/templates/' + id;

        return new Promise((resolve, reject) => {
            lastValueFrom(this.http.put<GeoTemplate>(this.env.apiUrl + templatePath, template)).then(
                templateResponse => {
                    resolve(Template.fromDTO(templateResponse, this.translate));
                },
                error => {
                    reject(error);
                }
            );
        });
    }

    // Insert new field to editor functions
    public pushFieldNamesToLists(templateFields: Field[]): { fieldDisplayNameList: string[]; fieldNameList: string[] } {
        const fieldDisplayNameList: string[] = [];
        const fieldNameList: string[] = [];

        const pushFieldName = (field: Field) => {
            fieldDisplayNameList.push(field.displayName);
            fieldNameList.push(field.name);
        };

        templateFields.forEach(field => {
            if (field.type === LayoutFieldType.Group && field.fields) {
                field.fields.forEach((subField: Field) => {
                    pushFieldName(subField);
                });
            }
            pushFieldName(field);
        });

        return { fieldDisplayNameList, fieldNameList };
    }

    public insertFieldToTemplate(templateContext: Template, newField: Field, position?: number[]): void {
        if (position === undefined) {
            templateContext.fields.push(newField);
        } else if (position[1] !== undefined) {
            let groupField = templateContext.fields[position[0]] as GroupField;
            if (!groupField.fields) {
                groupField.fields = [];
            }
            groupField.fields.splice(position[1], 0, newField);
        } else {
            templateContext.fields.splice(position[0], 0, newField);
        }
    }

    public getFieldDisplayName(
        fieldType: InsertNewFieldOptions['fieldType'],
        translate: TranslationService,
        selectedField: FieldDefinition,
        sensorWorkflow?: InsertNewFieldOptions['sensorWorkflow'],
        sensorChildField?: InsertNewFieldOptions['sensorChildField'],
        allAutoFieldSection?: InsertNewFieldOptions['allAutoFieldSection']
    ): string {
        if (sensorChildField) {
            return translate.instant(sensorChildField.displayName);
        } else if (sensorWorkflow) {
            return translate.instant(sensorWorkflow.workflowName);
        } else if (allAutoFieldSection && fieldType === LayoutFieldType.PageHeader) {
            return translate.instant('TC.Common.AutoFields');
        } else {
            return translate.instant(selectedField.application.layoutSchema.label);
        }
    }

    public generateNewNames(
        fieldType: InsertNewFieldOptions['fieldType'],
        fieldDisplayName: string,
        fieldDisplayNameList: string[],
        fieldNameList: string[],
        sequenceIncreaseValue?: number
    ): { newDisplayName: string; newName: string } {
        const newDisplayName = UtilitiesService.getNewName(
            fieldDisplayName,
            fieldDisplayNameList,
            false,
            sequenceIncreaseValue
        );

        const newName = UtilitiesService.getNewName(
            fieldType.toLowerCase(),
            fieldNameList,
            true,
            sequenceIncreaseValue,
            false
        );

        return { newDisplayName, newName };
    }

    public createNewProperties(
        fieldType: InsertNewFieldOptions['fieldType'],
        newDisplayName: string,
        newName: string,
        translate: TranslationService,
        sensorChildField?: InsertNewFieldOptions['sensorChildField'],
        duplicateGroupField?: InsertNewFieldOptions['duplicateGroupField'],
        duplicateChildField?: InsertNewFieldOptions['duplicateChildField']
    ): Partial<Field> {
        let newProperties: Partial<Field> = {
            uuid: UUID.generate(),
            type: fieldType,
            displayName: newDisplayName,
            name: newName,
            status: FieldStatus.NEW
        };

        if (sensorChildField) {
            newProperties = {
                ...newProperties,
                sensorMappingId: sensorChildField.mappingId,
                scale: sensorChildField.decimalPlaces,
                values: sensorChildField.codedValues?.map(value => ({
                    ...value,
                    description: translate.instant(value.description)
                }))
            };
        } else if (duplicateGroupField) {
            newProperties = {
                ...newProperties,
                displayName: duplicateGroupField.displayName
            };
        } else if (duplicateChildField) {
            newProperties = {
                ...duplicateChildField,
                uuid: UUID.generate(),
                type: fieldType,
                name: newName,
                status: FieldStatus.NEW
            };
        }

        return newProperties;
    }
}
